import { FC, useState } from 'react';
import { Userpilot } from 'userpilot';
import { USERPILOT_EVENTS } from 'utils/constants';
import intl from 'react-intl-universal';
import {
  IconButton,
  Tooltip,
  tailwindOverride,
} from '@getsynapse/design-system';
import TaskActualHoursModal from 'Pages/TaskDetailPage/ActualHoursModal/ActualHoursModal';
import { TaskWithAssignees } from 'types/store/projectTasksList';
import addHoursIcon from 'assets/icons/add-hours.svg';
import { PilotPagesToTrack } from 'utils/customTypes';

type TaskKeys = Pick<
  TaskWithAssignees,
  'id' | 'permissions' | 'status' | 'disabled'
>;

type QuickHoursAddingActionProps = {
  task: TaskKeys;
  refreshTasks: () => void;
  userPilotEventOrigin?:
    | PilotPagesToTrack.UNKNOWN
    | PilotPagesToTrack.HOME
    | PilotPagesToTrack.PROJECT_TASK
    | PilotPagesToTrack.CENTRALIZED_TASK;
};

const QuickHoursAddingAction: FC<QuickHoursAddingActionProps> = ({
  task,
  refreshTasks,
  userPilotEventOrigin = 'unknown',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canUpdateTask = task.permissions?.canEdit && !task.disabled;

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      Userpilot.track(USERPILOT_EVENTS.ADD_HOURS_QUICK_ACTION, {
        eventOrigin: userPilotEventOrigin,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Error tracking Userpilot event for add hours quick action',
        error
      );
    }

    setIsModalOpen(true);
  };

  if (!canUpdateTask) return <></>;

  return (
    <>
      <div className='flex flex-1 gap-x-1'>
        <Tooltip
          key={isModalOpen ? 'modal-open' : 'modal-closed'}
          openMode='hover1'
          timeout={0}
          showPopper
          position='topCenter'
          contentProps={{
            className: 'px-3 py-2 text-sm z-50 absolute',
          }}
          usePortal
          trigger={
            <div>
              <IconButton
                src={addHoursIcon}
                iconClassname='pointer-events-none'
                alt='add-hours'
                className={tailwindOverride(
                  'text-base text-neutral-dark p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200',
                  'hover:bg-neutral-lightest hover:shadow-allocation-table'
                )}
                onClick={handleIconClick}
                aria-label={`task-${task.id}__quick-edit-button`}
              />
            </div>
          }
        >
          {intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.ACTIONS.ADD_HOURS')}
        </Tooltip>
      </div>

      <TaskActualHoursModal
        taskId={task.id}
        canAddActualHours={canUpdateTask}
        shouldDisplayModal={isModalOpen}
        toggleDisplayModal={() => setIsModalOpen(!isModalOpen)}
        refresh={refreshTasks}
      />
    </>
  );
};

export default QuickHoursAddingAction;
